import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useParams } from 'react-router';
import { notify } from 'utils';
import AppLoader from '../../../../Components/AppLoader';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { useStyle as onboardingStyle } from '../..';
import ProjectAccordionList, { saveEditedProjects, rememberWorkingProject } from '../Project/ProjectAccordionList';

const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    intro: {
        marginBottom: theme.spacing(2),
    },
    introTitle: {
        color: '#1f2937',
        fontSize: '1.0625rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    introText: {
        color: '#647184',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        marginTop: 2,
    },
    emptyState: {
        padding: theme.spacing(5, 2),
        textAlign: 'center',
        color: '#647184',
        border: '1px dashed #d6dde6',
        borderRadius: 8,
    },
    continueBtn: {
        minWidth: 180,
        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
        },
    },
}));

const allocationMeta = (row) => [
    `${row?.course_count || 0} courses`,
    `${row?.assessment_count || 0} assessments`,
    `${row?.apprenticeship_count || 0} apprenticeships`,
].join(' · ');

const ProjectAllocation = ({ activeStep, handleBack, handleNext }) => {
    const classes = useStyles();
    const onboardingClasses = onboardingStyle();
    const { id } = useParams();
    const customerId = id || localStorage.getItem('customerUUID');

    const [projects, setProjects] = useState([]);
    const [expandedKey, setExpandedKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const editorRefs = useRef({});

    const getProjects = async () => {
        const projectsRes = await projectServices.allProjectsByCustomer(customerId);
        const rows = projectsRes?.results || [];
        setProjects(rows);
        return rows;
    };

    useEffect(() => {
        getProjects()
            .then((rows) => {
                const workingProject = localStorage.getItem('project_id');
                const initial = rows.find(row => row.project_uuid === workingProject) || rows[0];
                setExpandedKey(initial?.project_uuid || null);
            })
            .finally(() => setLoading(false));
    // Projects are loaded once for the fixed onboarding customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const items = projects.map(row => ({
        key: row.project_uuid,
        uuid: row.project_uuid,
        title: row.project_name || 'Untitled project',
        meta: allocationMeta(row),
        status: row.status,
    }));

    const handleContinue = async () => {
        if (!items.length) {
            notify("error", "Create a project on the Projects step before allocating licences.");
            return;
        }
        setSubmitLoading(true);
        try {
            const result = await saveEditedProjects(items, editorRefs);
            if (!result.ok) {
                setExpandedKey(result.failedKey);
                return;
            }
            if (result.savedCount) {
                localStorage.setItem('refreshlic', true);
                notify("success", result.savedCount === 1 ? 'Licence allocation saved.' : `Licence allocation saved for ${result.savedCount} projects.`);
            }
            rememberWorkingProject(result.lastSavedUuid, items.map(item => item.uuid));
            handleNext();
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <div className={classes.intro}>
                <Typography component="h2" className={classes.introTitle}>Allocate licences to projects</Typography>
                <Typography className={classes.introText}>
                    Open a project to allocate assessments, courses, apprenticeships and labs from the client licence pool.
                </Typography>
            </div>
            {loading ? (
                <AppLoader minHeight={240} />
            ) : items.length ? (
                <ProjectAccordionList
                    items={items}
                    section="licences"
                    expandedKey={expandedKey}
                    onExpand={setExpandedKey}
                    editorRefs={editorRefs}
                    customerId={customerId}
                />
            ) : (
                <div className={classes.emptyState}>
                    No projects yet. Go back to the Projects step to create one.
                </div>
            )}
            <div className={onboardingClasses.buttons}>
                <div className={onboardingClasses.wrapper}>
                    {activeStep !== 0 && (
                        <Button
                            type="button"
                            onClick={handleBack}
                            variant="outlined"
                            color="default"
                            disabled={submitLoading}
                            className={onboardingClasses.cancelBtn}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={handleContinue}
                        variant="contained"
                        color="primary"
                        disabled={loading || submitLoading}
                        endIcon={submitLoading ? null : <ArrowForwardIcon fontSize="small" />}
                        className={`${onboardingClasses.button} ${classes.continueBtn}`}
                    >
                        {submitLoading ? <CircularProgress size={18} /> : 'Continue to Users'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default ProjectAllocation;
